import { useEffect, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import { useSite } from "../../contexts/SiteContext";

export default function AdminContent() {
    const { refresh } = useSite();
    const [settings, setSettings] = useState(null);
    const [blocks, setBlocks] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("brand");

    useEffect(() => {
        Promise.all([api.get("/site"), api.get("/admin/blocks")])
            .then(([s, b]) => {
                setSettings(s.data.settings || {});
                const map = {};
                (b.data.blocks || []).forEach((blk) => {
                    map[blk.key] = blk.value;
                });
                setBlocks(map);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading || !settings) return <div className="text-gold">Loading…</div>;

    const updateSettings = (patch) => setSettings((s) => ({ ...s, ...patch }));
    const updateGroup = (group, patch) =>
        setSettings((s) => ({ ...s, [group]: { ...(s[group] || {}), ...patch } }));
    const updateBlock = (key, patch) =>
        setBlocks((b) => ({ ...b, [key]: { ...(b[key] || {}), ...patch } }));

    const saveSettings = async () => {
        try {
            await api.put("/admin/site", settings);
            toast.success("Site settings saved");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveBlock = async (key) => {
        try {
            await api.put(`/admin/blocks/${key}`, { value: blocks[key] });
            toast.success("Block saved");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const tabs = [
        { id: "brand", label: "Brand" },
        { id: "announcement", label: "Announcement" },
        { id: "nav", label: "Navigation" },
        { id: "hero", label: "Hero" },
        { id: "marquee", label: "Marquee" },
        { id: "categories", label: "Categories" },
        { id: "story", label: "Story strip" },
        { id: "footer", label: "Footer" },
    ];

    return (
        <div data-testid="admin-content">
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
            <h1 className="mt-2 font-display text-4xl text-white">Site content</h1>
            <p className="mt-2 text-sm text-white/50">
                Everything on the storefront — logo, hero, banners, story, footer — is editable here. Changes go live instantly.
            </p>

            <div className="mt-8 flex flex-wrap gap-2 border-b border-border-dim pb-px">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        data-testid={`content-tab-${t.id}`}
                        className={`-mb-px border-b-2 px-4 py-2 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                            activeTab === t.id ? "border-gold text-gold" : "border-transparent text-white/60 hover:text-white"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-8 max-w-3xl space-y-4">
                {activeTab === "brand" && (
                    <Panel title="Brand identity" onSave={saveSettings}>
                        <Text label="Brand name" value={settings.brand?.name || ""} onChange={(v) => updateGroup("brand", { name: v })} />
                        <Text label="Tagline" value={settings.brand?.tagline || ""} onChange={(v) => updateGroup("brand", { tagline: v })} />
                        <Text
                            label="Lettermark fallback (used when no logo uploaded)"
                            value={settings.brand?.logo_initial || ""}
                            onChange={(v) => updateGroup("brand", { logo_initial: v })}
                        />
                        <ImageUpload
                            label="Logo image (optional — falls back to lettermark)"
                            value={settings.brand?.logo_url || ""}
                            onChange={(url) => updateGroup("brand", { logo_url: url })}
                            testId="brand-logo"
                        />
                    </Panel>
                )}

                {activeTab === "announcement" && (
                    <Panel title="Top announcement bar" onSave={saveSettings}>
                        <Checkbox
                            label="Show announcement bar"
                            value={!!settings.announcement?.enabled}
                            onChange={(v) => updateGroup("announcement", { enabled: v })}
                        />
                        <Text
                            label="Message"
                            value={settings.announcement?.text || ""}
                            onChange={(v) => updateGroup("announcement", { text: v })}
                        />
                        <Text
                            label="Link"
                            value={settings.announcement?.link || ""}
                            onChange={(v) => updateGroup("announcement", { link: v })}
                        />
                    </Panel>
                )}

                {activeTab === "nav" && (
                    <Panel title="Navigation links" onSave={saveSettings}>
                        <ListEditor
                            items={settings.nav_links || []}
                            fields={[
                                { key: "label", label: "Label" },
                                { key: "to", label: "URL (e.g. /shop or /shop?cat=clothing)" },
                            ]}
                            onChange={(items) => updateSettings({ nav_links: items })}
                            emptyItem={{ label: "", to: "/" }}
                            testId="nav-links"
                        />
                    </Panel>
                )}

                {activeTab === "hero" && (
                    <Panel title="Homepage hero" onSave={() => saveBlock("home.hero")}>
                        <Text label="Eyebrow text" value={blocks["home.hero"]?.eyebrow || ""} onChange={(v) => updateBlock("home.hero", { eyebrow: v })} />
                        <Text label="Title line 1" value={blocks["home.hero"]?.title_line1 || ""} onChange={(v) => updateBlock("home.hero", { title_line1: v })} />
                        <Text label="Title line 2" value={blocks["home.hero"]?.title_line2 || ""} onChange={(v) => updateBlock("home.hero", { title_line2: v })} />
                        <Text label="Italic accent word" value={blocks["home.hero"]?.title_italic || ""} onChange={(v) => updateBlock("home.hero", { title_italic: v })} />
                        <Textarea label="Subheading" value={blocks["home.hero"]?.subheading || ""} onChange={(v) => updateBlock("home.hero", { subheading: v })} />
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Text label="Primary CTA label" value={blocks["home.hero"]?.primary_cta_label || ""} onChange={(v) => updateBlock("home.hero", { primary_cta_label: v })} />
                            <Text label="Primary CTA link" value={blocks["home.hero"]?.primary_cta_link || ""} onChange={(v) => updateBlock("home.hero", { primary_cta_link: v })} />
                            <Text label="Secondary CTA label" value={blocks["home.hero"]?.secondary_cta_label || ""} onChange={(v) => updateBlock("home.hero", { secondary_cta_label: v })} />
                            <Text label="Secondary CTA link" value={blocks["home.hero"]?.secondary_cta_link || ""} onChange={(v) => updateBlock("home.hero", { secondary_cta_link: v })} />
                        </div>
                        <ImageUpload
                            label="Hero image"
                            value={blocks["home.hero"]?.hero_image || ""}
                            onChange={(url) => updateBlock("home.hero", { hero_image: url })}
                            testId="hero-image"
                        />
                        <Text label="Note eyebrow" value={blocks["home.hero"]?.note_eyebrow || ""} onChange={(v) => updateBlock("home.hero", { note_eyebrow: v })} />
                        <Textarea label="Note text" rows={2} value={blocks["home.hero"]?.note_text || ""} onChange={(v) => updateBlock("home.hero", { note_text: v })} />
                    </Panel>
                )}

                {activeTab === "marquee" && (
                    <Panel title="Marquee strip" onSave={() => saveBlock("home.marquee")}>
                        <ListEditor
                            items={(blocks["home.marquee"]?.messages || []).map((m) => ({ message: m }))}
                            fields={[{ key: "message", label: "Message" }]}
                            onChange={(items) =>
                                updateBlock("home.marquee", { messages: items.map((i) => i.message).filter(Boolean) })
                            }
                            emptyItem={{ message: "" }}
                            testId="marquee-msgs"
                        />
                    </Panel>
                )}

                {activeTab === "categories" && (
                    <Panel title="Category cards" onSave={() => saveBlock("home.categories")}>
                        <Text label="Eyebrow" value={blocks["home.categories"]?.eyebrow || ""} onChange={(v) => updateBlock("home.categories", { eyebrow: v })} />
                        <Text label="Heading" value={blocks["home.categories"]?.title || ""} onChange={(v) => updateBlock("home.categories", { title: v })} />
                        <CardListEditor
                            items={blocks["home.categories"]?.items || []}
                            onChange={(items) => updateBlock("home.categories", { items })}
                            testId="cats"
                        />
                    </Panel>
                )}

                {activeTab === "story" && (
                    <Panel title="Story strip" onSave={() => saveBlock("home.story")}>
                        <Text label="Eyebrow" value={blocks["home.story"]?.eyebrow || ""} onChange={(v) => updateBlock("home.story", { eyebrow: v })} />
                        <Textarea label="Heading" value={blocks["home.story"]?.title || ""} onChange={(v) => updateBlock("home.story", { title: v })} />
                        <Textarea label="Body" value={blocks["home.story"]?.body || ""} onChange={(v) => updateBlock("home.story", { body: v })} />
                        <div className="grid grid-cols-2 gap-3">
                            <Text label="CTA label" value={blocks["home.story"]?.cta_label || ""} onChange={(v) => updateBlock("home.story", { cta_label: v })} />
                            <Text label="CTA link" value={blocks["home.story"]?.cta_link || ""} onChange={(v) => updateBlock("home.story", { cta_link: v })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <ImageUpload label="Image 1" value={blocks["home.story"]?.image_1 || ""} onChange={(v) => updateBlock("home.story", { image_1: v })} testId="story-img-1" />
                            <ImageUpload label="Image 2" value={blocks["home.story"]?.image_2 || ""} onChange={(v) => updateBlock("home.story", { image_2: v })} testId="story-img-2" />
                        </div>
                    </Panel>
                )}

                {activeTab === "footer" && (
                    <Panel title="Footer & contact" onSave={saveSettings}>
                        <Textarea label="Footer blurb" value={settings.footer?.blurb || ""} onChange={(v) => updateGroup("footer", { blurb: v })} />
                        <Text label="Footer tagline" value={settings.footer?.tagline || ""} onChange={(v) => updateGroup("footer", { tagline: v })} />
                        <Text label="Rights statement" value={settings.footer?.rights || ""} onChange={(v) => updateGroup("footer", { rights: v })} />
                        <Text label="Contact email" value={settings.contact?.email || ""} onChange={(v) => updateGroup("contact", { email: v })} />
                        <Text label="Instagram handle" value={settings.contact?.instagram || ""} onChange={(v) => updateGroup("contact", { instagram: v })} />
                        <Text label="Instagram URL" value={settings.contact?.instagram_url || ""} onChange={(v) => updateGroup("contact", { instagram_url: v })} />
                        <div className="border-t border-border-dim pt-4">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-gold">Footer pages</p>
                            <ListEditor
                                items={settings.footer_pages || []}
                                fields={[
                                    { key: "label", label: "Link label" },
                                    { key: "slug", label: "Page slug (must match a published page)" },
                                ]}
                                onChange={(items) => updateSettings({ footer_pages: items })}
                                emptyItem={{ label: "", slug: "" }}
                                testId="footer-pages"
                            />
                        </div>
                    </Panel>
                )}
            </div>
        </div>
    );
}

const inp = "w-full border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold";

function Panel({ title, children, onSave }) {
    return (
        <div className="border border-border-dim bg-surface p-6">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl text-white">{title}</h2>
                <button
                    onClick={onSave}
                    data-testid="panel-save"
                    className="inline-flex items-center gap-2 bg-gold px-4 py-2 text-[11px] uppercase tracking-widest text-obsidian hover:bg-gold-light"
                >
                    <Save className="h-3 w-3" /> Save
                </button>
            </div>
            <div className="mt-6 space-y-4">{children}</div>
        </div>
    );
}

function Text({ label, value, onChange }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)} className={`${inp} mt-2`} />
        </div>
    );
}

function Textarea({ label, value, onChange, rows = 3 }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
            <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={`${inp} mt-2`} />
        </div>
    );
}

function Checkbox({ label, value, onChange }) {
    return (
        <label className="flex items-center gap-3">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
            <span className="text-sm text-white">{label}</span>
        </label>
    );
}

function ListEditor({ items, fields, onChange, emptyItem, testId }) {
    const set = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const add = () => onChange([...items, { ...emptyItem }]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-3" data-testid={testId}>
            {items.map((it, i) => (
                <div key={i} className="flex flex-col gap-2 border border-border-dim p-3 md:flex-row md:items-end">
                    {fields.map((f) => (
                        <div key={f.key} className="flex-1">
                            <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">{f.label}</label>
                            <input
                                value={it[f.key] || ""}
                                onChange={(e) => set(i, { [f.key]: e.target.value })}
                                className={`${inp} mt-1`}
                            />
                        </div>
                    ))}
                    <button
                        onClick={() => remove(i)}
                        className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/60 hover:border-heart hover:text-heart"
                    >
                        <Trash2 className="h-3 w-3" />
                    </button>
                </div>
            ))}
            <button
                onClick={add}
                data-testid={`${testId}-add`}
                className="inline-flex items-center gap-2 border border-border-dim px-4 py-2 text-[11px] uppercase tracking-widest text-white/70 hover:border-gold hover:text-gold"
            >
                <Plus className="h-3 w-3" /> Add
            </button>
        </div>
    );
}

function CardListEditor({ items, onChange, testId }) {
    const set = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const add = () => onChange([...items, { name: "", blurb: "", href: "/shop", image: "" }]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-4" data-testid={testId}>
            {items.map((it, i) => (
                <div key={i} className="border border-border-dim p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-widest text-gold">Card {i + 1}</p>
                        <button onClick={() => remove(i)} className="text-white/60 hover:text-heart">
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Text label="Name" value={it.name} onChange={(v) => set(i, { name: v })} />
                        <Text label="Link URL" value={it.href} onChange={(v) => set(i, { href: v })} />
                        <div className="md:col-span-2">
                            <Text label="Blurb" value={it.blurb} onChange={(v) => set(i, { blurb: v })} />
                        </div>
                        <div className="md:col-span-2">
                            <ImageUpload label="Image" value={it.image} onChange={(v) => set(i, { image: v })} testId={`${testId}-img-${i}`} />
                        </div>
                    </div>
                </div>
            ))}
            <button
                onClick={add}
                className="inline-flex items-center gap-2 border border-border-dim px-4 py-2 text-[11px] uppercase tracking-widest text-white/70 hover:border-gold hover:text-gold"
            >
                <Plus className="h-3 w-3" /> Add card
            </button>
        </div>
    );
}
